import TariffService from "@/services/tariff.service";
import { Module } from "vuex";
import { SubscriptionResponse } from "@/struct/SubscriptionDataStruct";
import {
  ChangeTariffPayload,
  TariffArray,
  TariffOptionStruct,
} from "@/struct/TariffStruct";
import { convertKBToGB, formatDate } from "@/utils/format";
import { computed } from "vue";

const tariffService = new TariffService();

export interface TariffState {
  tariffArray: TariffArray[];
  availableTariffs: TariffArray[];
  subscriptionDetails: SubscriptionResponse;
  isLoadingSubscriptionDetails: boolean;
}

const getUnits = (id: number) => {
  switch (id) {
    case 1:
      return "min";
    case 2:
      return "sms";
    case 3:
      return "gb";
    default:
      break;
  }
};

const defaultOptions: TariffOptionStruct[] = [
  { value: 1, maximum: 1, id: "gb" },
  { value: 0, maximum: 1, id: "min" },
  { value: 0, maximum: 1, id: "sms" },
];

const getDefaultState = () => ({
  tariffArray: [] as TariffArray[],
  isLoadingSubscriptionDetails: false,
  subscriptionDetails: {} as SubscriptionResponse,
  availableTariffs: [] as TariffArray[],
});

const tariffModule: Module<TariffState, any> = {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    currentUserPhone: (state) =>
      state.subscriptionDetails?.mainProduct?.msisdnId || 0,

    walletAmount: (state) => state.subscriptionDetails?.wallet || "0",
    walletAmountCalc: (state) => {
      const walletAmount = state.subscriptionDetails?.wallet || "0";
      const value = Math.floor(parseInt(walletAmount));
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },
    addonOptions(state) {
      if (!state.subscriptionDetails?.additionalProducts) return [];
      return state.subscriptionDetails?.additionalProducts.map((item: any) => {
        const baseAddon = {
          id: item.productId,
          title: item.productName,
          date: formatDate(item.expiresOn),
          amount: item.activationPrice,
        };
        if (!item.unlimited) {
          const isGb = item.counterTypeId === 3;
          const isMin = item.counterTypeId === 1;
          return {
            ...baseAddon,
            unitData: {
              maximum: isGb
                ? convertKBToGB(item.amount)
                : isMin
                ? Math.floor(item.amount / 60)
                : item.amount,
              value: isGb
                ? convertKBToGB(item.liveAmount)
                : isMin
                ? Math.floor(item.liveAmount / 60)
                : item.liveAmount,
              id: getUnits(item.counterTypeId),
            },
          };
        } else return baseAddon;
      });
    },

    mainTariff(state) {
      if (!state.subscriptionDetails?.mainProduct) return null;

      const main = state.subscriptionDetails?.mainProduct;

      const tariffOptions = [
        {
          id: "min",
          value: Math.floor(main.voiceData.liveAmount / 60),
          maximum: Math.floor(main.voiceData.amount / 60),
        },
        {
          id: "sms",
          value: main.smsData.liveAmount,
          maximum: main.smsData.amount,
        },
        {
          id: "gb",
          value: convertKBToGB(main.internetData.liveAmount),
          maximum: convertKBToGB(main.internetData.amount),
        },
      ];

      let discountInfo = null;
      let finalPrice = main.mainProductActivationPrice;
      if (main.promotion) {
        finalPrice = main.mainProductFullPrice; // По вашей логике при промоушене берется FullPrice
        discountInfo = {
          amount: main.promotion.renewalDiscount,
          name: main.promotion.displayName,
        };
      }

      return {
        title: main.mainProductName,
        options: tariffOptions,
        nextDate: formatDate(main.renewalDate),
        amount: finalPrice,
        facility: main.facilityId,
        subscriptionId: main.subscriptionId,
        compatibleId: main.mainProductId,
        service: main.service,
        discount: discountInfo,
      };
    },
    displayOptions(state, getters) {
      return getters.mainTariff?.options?.length
        ? getters.mainTariff?.options
        : defaultOptions;
    },
    tariffPriceDiscount(state) {
      return (
        state.subscriptionDetails?.mainProduct?.mainProductActivationPrice ||
        "0"
      );
    },
  },
  mutations: {
    SET_SUBSCRIPTION_DETAILS(state, value: SubscriptionResponse) {
      state.subscriptionDetails = value;
    },
    SET_IS_LOADING_SUBSCRIPTION_DETAILS(state, isLoading: boolean) {
      state.isLoadingSubscriptionDetails = isLoading;
    },
    SET_AVAILABLE_TARIFFS(state, value: TariffArray[]) {
      state.availableTariffs = value;
    },
  },
  actions: {
    updateSubscriptionDetails({ commit, dispatch }) {
      commit("SET_IS_LOADING_SUBSCRIPTION_DETAILS", true);
      return tariffService
        .getSubscriptionDetails()
        .then((res) => {
          commit("SET_SUBSCRIPTION_DETAILS", res.data.response);
          const operator = res.data?.response?.mainProduct?.operator;
          dispatch("brand/syncOperatorBrand", operator, { root: true });
          return res;
        })
        .catch((error) => {
          console.log("Error Update Subscription Details");
          dispatch(
            "system/openModal",
            { presetKey: "general_error" },
            { root: true }
          );
        })
        .finally(() => {
          commit("SET_IS_LOADING_SUBSCRIPTION_DETAILS", false);
        });
    },

    changeTariff({ commit }, payload: ChangeTariffPayload) {
      return tariffService.changeTariff(payload);
    },
    updateOffers({ commit, getters }) {
      return tariffService
        .getOffers()
        .then((res) => {
          const offers = res?.data?.data;
          if (!Array.isArray(offers)) {
            console.error("Получен некорректный формат офферов");
            return false;
          }
          const currentCompatibleId = getters.mainTariff?.compatibleId;
          const filteredTariffs = offers.filter(
            (item) => item.ems_product_id !== currentCompatibleId
          );
          commit("SET_AVAILABLE_TARIFFS", filteredTariffs);
          return true;
        })
        .catch((err) => {
          console.error("Системная ошибка при обновлении офферов:", err);
          return false;
        });
    },
    clearTariffStore({ state }) {
      Object.assign(state, getDefaultState());
    },
  },
};

export default tariffModule;
